import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, PlusCircle, FilterIcon } from 'lucide-react';
import AppliedFilters from '@/components/AppliedFilters';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import DynamicFormModal from '@/components/DynamicFormModal';
import { useToast } from '@/components/ui/use-toast';
import { zonasColumns, columnsExcel } from '@/pages/parametrizacion/zonas/utils/zonasColumns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { fetchZonas, createZona, updateZona, deleteZona, exportZonas } from '@/pages/parametrizacion/zonas/Services/zonas.services';
import FilterDrawer from '@/components/FilterDrawer';
import ExportExcelButton from '@/components/ExportExcelButton';

const ITEMS_PER_PAGE = 10;
const DEFAULT_FILTERS = { estado: 'Activo' };

const ZonasPage = () => {
  const { toast } = useToast();
  const [displayedData, setDisplayedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [modalMode, setModalMode] = useState('create');

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const fetchAndSetZonas = useCallback(async () => {
    setIsLoading(true);
    try {
      const offset = currentPage;
      const { data, totalRecords } = await fetchZonas({ limit: ITEMS_PER_PAGE, offset, query: filters });
      setDisplayedData(data);
      setTotalItems(totalRecords);
    } catch (error) {
      console.error('Error al obtener zonas:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar las zonas. Por favor, intenta de nuevo.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, filters, toast]);

  useEffect(() => {
    fetchAndSetZonas();
  }, [fetchAndSetZonas]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setCurrentPage(1);
  };

  const handleRemoveFilter = (filterKey) => {
    const newFilters = { ...filters };
    delete newFilters[filterKey];
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleCreateZona = () => {
    setCurrentItem(null);
    setModalMode('create');
    setIsModalOpen(true);
  };

  const handleEditZona = (zona) => {
    setCurrentItem(zona);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const handleDeleteZona = (zona) => {
    setItemToDelete(zona);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      await deleteZona(itemToDelete._id);
      toast({
        title: "Zona eliminada",
        description: "La zona ha sido eliminada exitosamente.",
      });
      fetchAndSetZonas();
    } catch (error) {
      console.error('Error al eliminar zona:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar la zona. Por favor, intenta de nuevo.",
        variant: "destructive",
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleSaveZona = async (zonaData) => {
    try {
      if (modalMode === 'create') {
        await createZona(zonaData);
        toast({
          title: "Zona creada",
          description: "La zona ha sido creada exitosamente.",
        });
      } else {
        await updateZona(currentItem._id, zonaData);
        toast({
          title: "Zona actualizada",
          description: "La zona ha sido actualizada exitosamente.",
        });
      }
      setIsModalOpen(false);
      fetchAndSetZonas();
    } catch (error) {
      console.error('Error al guardar zona:', error);
      toast({
        title: "Error",
        description: `No se pudo ${modalMode === 'create' ? 'crear' : 'actualizar'} la zona. Por favor, intenta de nuevo.`,
        variant: "destructive",
      });
    }
  };

  const handleAction = (action, item) => {
    switch (action) {
      case 'edit':
        handleEditZona(item);
        break;
      case 'delete':
        handleDeleteZona(item);
        break;
      default:
        break;
    }
  };

  const filterFields = [
    {
      id: 'nombre',
      label: 'Nombre',
      type: 'text',
      placeholder: 'Buscar por nombre...',
    },
    {
      id: 'descripcion',
      label: 'Descripción',
      type: 'text',
      placeholder: 'Buscar por descripción...',
    },
    {
      id: 'estado',
      label: 'Estado',
      type: 'select',
      options: [
        { value: 'Activo', label: 'Activo' },
        { value: 'Inactivo', label: 'Inactivo' },
      ],
    },
  ];

  return (
    <div className="container mx-auto p-6 space-y-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <MapPin className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold">Zonas</CardTitle>
                <CardDescription>Gestión de zonas geográficas</CardDescription>
              </div>
            </div>
            <div className="flex gap-2">
              <ExportExcelButton
                data={displayedData}
                columns={columnsExcel}
                fileName="zonas"
                fetchAllData={exportZonas}
                currentFilters={filters}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsDrawerOpen(true)}
              >
                <FilterIcon className="mr-2 h-4 w-4" />
                Filtrar
              </Button>
              <Button onClick={handleCreateZona} size="sm">
                <PlusCircle className="mr-2 h-4 w-4" />
                Crear Zona
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <AppliedFilters
              filters={filters}
              fields={filterFields}
              onRemoveFilter={handleRemoveFilter}
              onClearFilters={handleClearFilters}
              defaultFilters={DEFAULT_FILTERS}
            />
            <DataTable
              data={displayedData}
              columns={zonasColumns}
              isLoading={isLoading}
              onAction={handleAction}
            />
            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(totalItems / ITEMS_PER_PAGE)}
              onPageChange={handlePageChange}
              totalItems={totalItems}
              itemsPerPage={ITEMS_PER_PAGE}
            />
          </CardContent>
        </Card>
      </motion.div>

      <DynamicFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveZona}
        fields={zonasColumns.filter(col => col.id !== 'opciones')}
        item={currentItem}
        title={modalMode === 'create' ? 'Crear Zona' : 'Editar Zona'}
        mode={modalMode}
      />

      <FilterDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onApplyFilters={handleFilterChange}
        fields={filterFields}
        currentFilters={filters}
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará permanentemente la zona
              <strong> {itemToDelete?.nombre}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ZonasPage;
